import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ServerMessageSchema,
  type LiveSnapshot,
  type ServerMessage,
  isHostOrCoHost,
  type ClientRole,
  DEV_MEETING_ID,
  encodeWsConnectSecret,
} from '@zcs/shared';
import {
  fetchUserContextStatus,
  parseUserContextStatus,
  refreshZoomContextToken,
  type ZoomAccess,
  type ZoomUserContextStatus,
} from './zoomAccess';
import { AuthorizeBanner } from './AuthorizeBanner';
import { PositionButton } from './PositionButton';
import { StatusBanner } from './StatusBanner';
import { ControlRoster } from './ControlRoster';
import { useMeetingParticipants } from './meetingParticipants';
import { mapZoomRole } from '@zcs/shared';
import { ensureZoomSdkConfigured, resetZoomSdkConfigured, zoomSdk } from './zoomSdk';
import { withTimeout } from './promise-timeout';

const SDK_CALL_TIMEOUT_MS = 10_000;

const WS_URL = import.meta.env.VITE_RELAY_WS_URL ?? 'ws://localhost:8080';
const SHOW_ID = import.meta.env.VITE_SHOW_ID ?? 'show-001';
const STALE_MS = 30_000;
const SYNC_MS = 10_000;
const DEBOUNCE_MS = 500;
const PENDING_TIMEOUT_MS = 8_000;

type ReadyZoomAccess = Extract<ZoomAccess, { status: 'ready' }>;

/** Parse meeting id from dev context token (dev:user:role:meeting). */
function meetingIdFromContextToken(contextToken: string): string | undefined {
  if (!contextToken.startsWith('dev:')) return undefined;
  const parts = contextToken.split(':');
  return parts[3]?.trim() || DEV_MEETING_ID;
}

export default function App({ zoom }: { zoom: ReadyZoomAccess }) {
  const [userRole, setUserRole] = useState<ClientRole>(zoom.userRole);
  const [userContextStatus, setUserContextStatus] = useState<ZoomUserContextStatus>(
    zoom.userContextStatus,
  );
  const [authorizing, setAuthorizing] = useState(false);
  const [connectNonce, setConnectNonce] = useState(0);
  const [snapshot, setSnapshot] = useState<LiveSnapshot | null>(null);
  const [canControl, setCanControl] = useState(false);
  const [relayBridgeUp, setRelayBridgeUp] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [grantedUserIds, setGrantedUserIds] = useState<string[]>([]);
  const [wsStatus, setWsStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [clock, setClock] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const lastStateAtRef = useRef<number>(0);
  const debounceRef = useRef<Map<string, number>>(new Map());
  const pendingTimerRef = useRef<Map<string, number>>(new Map());

  const companionUp = snapshot?.bridgeConnected ?? relayBridgeUp;
  const isStale =
    wsStatus === 'connected' &&
    relayBridgeUp &&
    lastStateAtRef.current > 0 &&
    Date.now() - lastStateAtRef.current > STALE_MS;
  const controlBlocked = !relayBridgeUp || !companionUp || isStale;

  useEffect(() => {
    setUserRole(zoom.userRole);
  }, [zoom.userRole]);

  useEffect(() => {
    setUserContextStatus(zoom.userContextStatus);
  }, [zoom.userContextStatus]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const config = await ensureZoomSdkConfigured();
      if (!config || cancelled) return;
      if ((config.unsupportedApis ?? []).includes('onMyUserContextChange')) return;

      try {
        zoomSdk.onMyUserContextChange((event: { role?: string; status?: string }) => {
          if (cancelled) return;
          void (async () => {
            const status = event.status
              ? parseUserContextStatus(event)
              : await fetchUserContextStatus();
            if (cancelled) return;
            setUserContextStatus(status);
            if (event.role) {
              setUserRole(mapZoomRole(event.role));
            }
            if (status === 'authorized') {
              resetZoomSdkConfigured();
              await ensureZoomSdkConfigured();
              if (!cancelled) setConnectNonce((n) => n + 1);
            }
          })();
        });
      } catch {
        // Event unsupported in this client build.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAuthorize = () => {
    setAuthorizing(true);
    void (async () => {
      try {
        const config = await ensureZoomSdkConfigured();
        if (config?.unsupportedApis?.includes('promptAuthorize')) {
          console.warn('[ZCS] promptAuthorize unsupported');
          return;
        }
        await withTimeout(async () => zoomSdk.promptAuthorize(), SDK_CALL_TIMEOUT_MS, 'promptAuthorize');
      } catch (err) {
        console.warn('[ZCS] promptAuthorize failed', err instanceof Error ? err.message : err);
      } finally {
        setAuthorizing(false);
      }
    })();
  };

  const send = useCallback(
    (msg: { action: string; showId?: string; positionId?: string; userId?: string }) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(msg));
      }
    },
    [],
  );

  const handleServerMessage = useCallback((msg: ServerMessage) => {
    switch (msg.type) {
      case 'state':
        setSnapshot(msg.snapshot);
        lastStateAtRef.current = Date.now();
        setPendingIds((prev) => {
          if (prev.size === 0) return prev;
          const liveCount = msg.snapshot.positions.filter((p) => p.live).length;
          const next = new Set(prev);
          for (const id of prev) {
            const pos = msg.snapshot.positions.find((p) => p.id === id);
            if (pos?.live && liveCount === 1) {
              next.delete(id);
              const timer = pendingTimerRef.current.get(id);
              if (timer) {
                window.clearTimeout(timer);
                pendingTimerRef.current.delete(id);
              }
            }
          }
          return next.size === prev.size ? prev : next;
        });
        break;
      case 'bridgePresence':
        setRelayBridgeUp(msg.connected);
        break;
      case 'permissions':
        setCanControl(msg.canControl);
        if (msg.grantedUserIds) setGrantedUserIds(msg.grantedUserIds);
        break;
      case 'error':
        setError(msg.message);
        break;
      case 'pong':
        break;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let ws: WebSocket | null = null;
    let pingInterval = 0;
    let syncInterval = 0;
    let staleInterval = 0;

    setWsStatus('connecting');
    setRelayBridgeUp(false);
    setSnapshot(null);
    lastStateAtRef.current = 0;

    void (async () => {
      const contextToken = await refreshZoomContextToken(zoom.contextToken);
      if (cancelled) return;

      const url = new URL(WS_URL);
      url.searchParams.set('role', 'panel');
      url.searchParams.set('showId', SHOW_ID);
      url.searchParams.set('userId', zoom.userId);
      const meetingId = meetingIdFromContextToken(contextToken);
      if (meetingId) {
        url.searchParams.set('meetingId', meetingId);
      }
      if (!zoom.meetingUUID) {
        console.warn('[ZCS] no meetingUUID for relay connect — WebSocket may be rejected');
      } else {
        url.searchParams.set('meetingUUID', zoom.meetingUUID);
      }

      ws = new WebSocket(url.toString(), encodeWsConnectSecret(contextToken));
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setWsStatus('connected');
        send({ action: 'sync', showId: SHOW_ID });
      };

      ws.onmessage = (event) => {
        try {
          const parsed = ServerMessageSchema.parse(JSON.parse(event.data));
          console.info('[ZCS] ws recv', parsed.type);
          handleServerMessage(parsed);
        } catch (err) {
          console.warn(
            '[ZCS] ws frame rejected',
            err instanceof Error ? err.message : 'parse error',
          );
        }
      };

      ws.onclose = () => {
        if (!cancelled) setWsStatus('disconnected');
      };
      ws.onerror = () => {
        if (!cancelled) setWsStatus('disconnected');
      };

      pingInterval = window.setInterval(() => send({ action: 'ping', showId: SHOW_ID }), 30_000);
      syncInterval = window.setInterval(() => send({ action: 'sync', showId: SHOW_ID }), SYNC_MS);
      staleInterval = window.setInterval(() => setClock((n) => n + 1), 5_000);
    })();

    return () => {
      cancelled = true;
      window.clearInterval(pingInterval);
      window.clearInterval(syncInterval);
      window.clearInterval(staleInterval);
      ws?.close();
      wsRef.current = null;
    };
  }, [zoom.contextToken, zoom.userId, zoom.meetingUUID, connectNonce, send, handleServerMessage]);

  void clock;

  const handlePress = (positionId: string) => {
    if (!canControl || controlBlocked) return;

    const lastPress = debounceRef.current.get(positionId) ?? 0;
    if (Date.now() - lastPress < DEBOUNCE_MS) return;
    debounceRef.current.set(positionId, Date.now());

    setPendingIds((prev) => new Set(prev).add(positionId));
    setError(null);
    send({ action: 'press', positionId });

    const existing = pendingTimerRef.current.get(positionId);
    if (existing) window.clearTimeout(existing);
    pendingTimerRef.current.set(
      positionId,
      window.setTimeout(() => {
        pendingTimerRef.current.delete(positionId);
        setPendingIds((prev) => {
          if (!prev.has(positionId)) return prev;
          const next = new Set(prev);
          next.delete(positionId);
          return next;
        });
        setError(
          'Companion did not confirm the switch. Check bridge/config.json locations and liveMatch/liveColor.',
        );
      }, PENDING_TIMEOUT_MS),
    );
  };

  const handleGrant = (userId: string) => {
    send({ action: 'grant', userId });
  };

  const handleRevoke = (userId: string) => {
    send({ action: 'revoke', userId });
  };

  const showRoster = isHostOrCoHost(userRole);
  const participantsState = useMeetingParticipants(showRoster);
  const switching = pendingIds.size > 0;

  return (
    <div className="app">
      <header className="header">
        <h1>Live Switcher</h1>
      </header>

      <AuthorizeBanner
        userContextStatus={userContextStatus}
        onAuthorize={handleAuthorize}
        authorizing={authorizing}
      />

      <StatusBanner
        wsStatus={wsStatus}
        relayBridgeUp={relayBridgeUp}
        companionUp={companionUp}
        isStale={isStale}
        canControl={canControl}
        error={error}
      />

      <div className="positions">
        {snapshot?.positions.map((pos) => {
          const pending = pendingIds.has(pos.id);
          // Companion SUB-STATE updates arrive one button at a time; suppress stale live
          // on other positions while the requested switch is in flight.
          const live = switching && !pending ? false : pos.live;
          return (
            <PositionButton
              key={pos.id}
              label={pos.label}
              live={live}
              pending={pending}
              disabled={!canControl || controlBlocked}
              onPress={() => handlePress(pos.id)}
            />
          );
        }) ?? (
          <p className="loading">Waiting for state...</p>
        )}
      </div>

      {showRoster && (
        <ControlRoster
          selfUserId={zoom.userId}
          participantsState={participantsState}
          grantedUserIds={grantedUserIds}
          onGrant={handleGrant}
          onRevoke={handleRevoke}
        />
      )}

      {!showRoster && wsStatus === 'connected' && (
        <p className="roster-participant-id-hint">
          Need control access? Ask the host to grant{' '}
          <code className="roster-id-code">{zoom.userId}</code>
        </p>
      )}
    </div>
  );
}
